import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { dateFnsLocale } from '@/lib/i18nDate';
import { Bell, Briefcase, CalendarClock, CheckCircle2, Clock, MessageCircle, ShieldCheck, ShieldX, UserMinus, UserPlus, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { Notification } from '@/hooks/useNotifications';
import { profileOfflineItems } from '@/lib/profilePublish';
import { identityDeclinedReasonKey } from '@/lib/identityNotifications';

const ICONS: Record<string, typeof Bell> = {
  invite_received: UserPlus,
  invite_accepted: CheckCircle2,
  invite_expiring: Clock,
  link_disconnected: UserMinus,
  invite_rejected: XCircle,
  message_received: MessageCircle,
  viewing_expired: Clock,
  viewing_request: CalendarClock,
  viewing_declined: XCircle,
  profile_info_requested: Briefcase,
  profile_info_approved: CheckCircle2,
  profile_info_rejected: XCircle,
  profile_published: CheckCircle2,
  identity_verified: ShieldCheck,
  identity_declined: ShieldX,
  identity_started_reminder: Clock,
};

/** Interpolation values come from the notification's jsonb payload. */
function payloadVars(payload: Notification['payload']): Record<string, string> {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (typeof v === 'string' || typeof v === 'number') out[k] = String(v);
  }
  return out;
}

interface Props {
  notification: Notification;
  onActivate?: (id: string) => void;
  /** Accept/reject an invite inline (Path C). Receives the link id. */
  onRespondInvite?: (notificationId: string, linkId: string, action: 'accept' | 'reject') => Promise<unknown>;
  /** Approve/reject a "Praca i referencje" consent request inline (co-tenant, not in the thread). */
  onRespondProfileInfo?: (notificationId: string, requestId: string, approve: boolean) => Promise<void>;
  /** True once the user has already acted on this invite / request — hides the buttons. */
  responded?: boolean;
  /** Keep the "new" dot + tint lit even after the row is marked seen (so a freshly-arrived item stays
   *  visually identifiable for a few seconds after the panel auto-marks everything seen). */
  highlighted?: boolean;
}

const NotificationItem = ({ notification, onActivate, onRespondInvite, onRespondProfileInfo, responded, highlighted }: Props) => {
  const { t, i18n } = useTranslation();
  const Icon = ICONS[notification.type] ?? Bell;
  const vars = payloadVars(notification.payload);
  if (notification.type === 'profile_offline') vars.items = profileOfflineItems(notification.payload, t);
  if (notification.type === 'identity_declined') vars.reason = t(identityDeclinedReasonKey(notification.payload));
  const [busy, setBusy] = useState(false);

  // An invite_received that still carries a link id can be accepted/rejected
  // right here, as long as the user hasn't already acted on it this session.
  const linkId = typeof vars.linkId === 'string' ? vars.linkId : '';
  const showInviteActions =
    notification.type === 'invite_received' && !!linkId && !!onRespondInvite && !responded;

  // A profile_info_requested carrying a request id → the co-tenant approves/rejects it right here
  // (they have no landlord thread to do it in).
  const requestId = typeof vars.request_id === 'string' ? vars.request_id : '';
  const showConsentActions =
    notification.type === 'profile_info_requested' && !!requestId && !!onRespondProfileInfo && !responded;

  const respond = async (action: 'accept' | 'reject') => {
    if (busy || !onRespondInvite) return;
    setBusy(true);
    try {
      await onRespondInvite(notification.id, linkId, action);
    } finally {
      setBusy(false);
    }
  };

  const respondConsent = async (approve: boolean) => {
    if (busy || !onRespondProfileInfo) return;
    setBusy(true);
    try {
      await onRespondProfileInfo(notification.id, requestId, approve);
    } finally {
      setBusy(false);
    }
  };

  const title = t(`notifications.types.${notification.type}.title`, {
    ...vars,
    defaultValue: notification.type,
  });
  const body = t(`notifications.types.${notification.type}.body`, {
    ...vars,
    defaultValue: '',
  });

  // Guard against a missing/invalid timestamp: formatDistanceToNow throws on an
  // Invalid Date, which would crash the whole notifications page.
  const createdAt = notification.created_at ? new Date(notification.created_at) : null;
  const timeAgo =
    createdAt && !Number.isNaN(createdAt.getTime())
      ? formatDistanceToNow(createdAt, { addSuffix: true, locale: dateFnsLocale(i18n.language) })
      : '';
  const isUnseen = notification.status === 'unseen' || highlighted === true;

  const content = (
    <div className="flex gap-3 p-3">
      <div className="mt-0.5 shrink-0 text-muted-foreground">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {body && <p className="mt-0.5 text-sm text-muted-foreground">{body}</p>}
        <p className="mt-1 text-xs text-muted-foreground">{timeAgo}</p>
      </div>
      {isUnseen && (
        <span
          aria-label={t('notifications.unseen')}
          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary"
        />
      )}
    </div>
  );

  const className = cn(
    'block w-full text-left transition-colors hover:bg-accent',
    isUnseen && 'bg-accent/40',
  );

  // Invite with inline actions: render as a static block (not a navigating
  // Link) so the Accept/Reject buttons aren't nested inside an anchor.
  if (showInviteActions) {
    return (
      <div className={cn('w-full', isUnseen && 'bg-accent/40')}>
        {content}
        <div className="flex gap-2 px-3 pb-3">
          <Button size="sm" disabled={busy} onClick={() => respond('accept')}>
            {t('co_tenants.action_accept')}
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => respond('reject')}>
            {t('co_tenants.action_reject')}
          </Button>
        </div>
      </div>
    );
  }

  // Consent request with inline actions (co-tenant approves/rejects "Praca i referencje").
  if (showConsentActions) {
    return (
      <div className={cn('w-full', isUnseen && 'bg-accent/40')}>
        {content}
        <div className="flex gap-2 px-3 pb-3">
          <Button size="sm" disabled={busy} onClick={() => respondConsent(true)}>
            {t('consent.tenant_approve')}
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => respondConsent(false)}>
            {t('consent.tenant_reject')}
          </Button>
        </div>
      </div>
    );
  }

  if (notification.action_url) {
    return (
      <Link to={notification.action_url} className={className} onClick={() => onActivate?.(notification.id)}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className={className} onClick={() => onActivate?.(notification.id)}>
      {content}
    </button>
  );
};

export default NotificationItem;
